import React, { useState } from 'react';
import { Box, Typography, Link, Button, Paper } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { limitStringLength } from '@pv/common/utils';
import { FullScreen } from '@pv/common/components';
import {
  SUSPICIOUS_MESSAGE_REPORTS_QUERY,
  IGNORE_SUSPICIOUS_MESSAGE_REPORT_MUTATION,
  CREATE_FRAUD_TOKEN_FROM_REPORT_MUTATION,
} from './graphql';
import { reportSx } from './styles';
import {
  SuspiciousMessageReportsQuery,
  SuspiciousMessageReportsQueryVariables,
  IgnoreSuspiciousMessageReportMutation,
  IgnoreSuspiciousMessageReportMutationVariables,
  CreateFraudTokenFromReportMutation,
  CreateFraudTokenFromReportMutationVariables,
} from '@pv/common/graphql/operations';
import { phonePrint } from '@pv/common/utils';
import { ReviewSuspiciousReportDialog } from './ReviewSuspiciousReportDialog';
import { SuspiciousMessageReportDetailsDialog } from './SuspiciousMessageReportDetailsDialog';
export type SuspiciousMessageReport =
  SuspiciousMessageReportsQuery['suspiciousMessageReports'][number];

export const SuspiciousMessageReportsTable = () => {
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] =
    useState<SuspiciousMessageReport | null>(null);

  const { data, loading, refetch } = useQuery<
    SuspiciousMessageReportsQuery,
    SuspiciousMessageReportsQueryVariables
  >(SUSPICIOUS_MESSAGE_REPORTS_QUERY, {
    variables: { status: 'pending' },
  });

  const rows = data?.suspiciousMessageReports || [];
  const [ignoreSuspiciousMessageReport] = useMutation<
    IgnoreSuspiciousMessageReportMutation,
    IgnoreSuspiciousMessageReportMutationVariables
  >(IGNORE_SUSPICIOUS_MESSAGE_REPORT_MUTATION);

  const [createFraudTokenFromReport] = useMutation<
    CreateFraudTokenFromReportMutation,
    CreateFraudTokenFromReportMutationVariables
  >(CREATE_FRAUD_TOKEN_FROM_REPORT_MUTATION);

  const handleOpenReviewDialog = (report: SuspiciousMessageReport) => {
    setSelectedReport(report);
    setReviewDialogOpen(true);
  };

  const handleCreateToken = async (
    tokenType: string,
    value: string,
    notes: string
  ) => {
    if (!selectedReport) return;

    try {
      await createFraudTokenFromReport({
        variables: {
          reportId: selectedReport.id,
          tokenType,
          value,
          notes: notes || null,
        },
      });
      refetch();
    } catch (error) {
      console.error('Error creating fraud token:', error);
    }
  };

  const handleViewDetails = (report: SuspiciousMessageReport) => {
    setSelectedReport(report);
    setDetailsDialogOpen(true);
  };

  const handleIgnore = async (notes: string) => {
    if (!selectedReport) return;

    try {
      await ignoreSuspiciousMessageReport({
        variables: {
          id: selectedReport.id,
          notes: notes || null,
        },
      });
      refetch();
    } catch (error) {
      console.error('Error ignoring report:', error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Reported',
      width: 120,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'event',
      headerName: 'Event',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2">{params.value.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Venue:{' '}
            <Link href={`/admin/venues/${params.value.venue.id}`}>
              {params.value.venue.name}
            </Link>
          </Typography>
        </Box>
      ),
    },
    {
      field: 'planner',
      headerName: 'Planner',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const planner = params.row.event.planner;
        return (
          <Box>
            <Typography variant="body2">{planner.fullName}</Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {planner.email}
            </Typography>
            {planner.phone && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
              >
                {phonePrint(planner.phone)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'message',
      headerName: 'Message Preview',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {limitStringLength(200, params.value.plainTextFromHtml || '')}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.value || '-',
    },
    {
      field: 'reportedBy',
      headerName: 'Reported By',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.value?.email || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleViewDetails(params.row)}
          >
            View Details
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenReviewDialog(params.row)}
          >
            Review
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <FullScreen paddingBottom={24}>
      {({ height }) => (
        <Paper sx={{ height, width: '100%', p: 2 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Suspicious Message Reports
          </Typography>

          <DataGridPro
            rows={rows}
            columns={columns}
            loading={loading}
            hideFooter={true}
            disableRowSelectionOnClick
            sx={{
              height: 'calc(100% - 105px)',
              ...reportSx,
            }}
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            getRowHeight={() => 'auto'}
          />

          <ReviewSuspiciousReportDialog
            open={reviewDialogOpen}
            report={selectedReport}
            onClose={() => {
              setReviewDialogOpen(false);
              setSelectedReport(null);
            }}
            onCreateToken={handleCreateToken}
            onIgnore={handleIgnore}
          />

          <SuspiciousMessageReportDetailsDialog
            open={detailsDialogOpen}
            plannerId={selectedReport?.event.planner?.id || ''}
            onClose={() => {
              setDetailsDialogOpen(false);
              setSelectedReport(null);
            }}
          />
        </Paper>
      )}
    </FullScreen>
  );
};

const NoRowsOverlay = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Suspicious Message Reports
      </Typography>
      <Typography variant="body2" color="text.secondary">
        All caught up! No pending reports to review.
      </Typography>
    </Box>
  );
};
